import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { ReactNode, useState } from 'react'

import { BackedByChart } from '../backed-by-chart/BackedByChart'
import { LiquidityChart } from '../liquidity-chart/LiquidityChart'
import { CollateralAtRiskChart } from './components/CollateralAtRiskChart'
import { SavingsRateChart } from './components/SavingsRateChart'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import {
  isSavingsChartId,
  SAVINGS_CHART_IDS,
  SavingsChartDataById,
  SavingsChartEnabledById,
  type SavingsChartId,
} from '@/features/savings/logic/useSavingsCharts'
import { Switch } from '@/ui/atoms/switch/Switch'
import { ChartTab, ChartTabsPanel, createChartTab } from '@/ui/charts/components/ChartTabsPanel'

export interface SavingsChartsProps {
  chartDataById: SavingsChartDataById
  isChartEnabledById: SavingsChartEnabledById
  savingsToken: Token
  underlyingToken: Token
  selectedTabId?: SavingsChartId
  onSelectedTabIdChange?: (tabId: SavingsChartId) => void
}

export function SavingsCharts({
  chartDataById,
  isChartEnabledById,
  savingsToken,
  underlyingToken,
  selectedTabId,
  onSelectedTabIdChange,
}: SavingsChartsProps) {
  const [usdsBackingDetailsExpanded, setUsdsBackingDetailsExpanded] = useState(false)
  const savingsRateChartData = chartDataById[SAVINGS_CHART_IDS.savingsRate]
  const savingsBackedByChartData = chartDataById[SAVINGS_CHART_IDS.savingsBackedBy]
  const savingsLiquidityChartData = chartDataById[SAVINGS_CHART_IDS.liquidity]
  const collateralAtRiskChartData = chartDataById[SAVINGS_CHART_IDS.collateralAtRisk]

  const detailsSwitch = savingsBackedByChartData.groupUsdsDetails ? (
    <div className="hidden items-center gap-2 lg:flex">
      <label className="typography-label-4 text-secondary">Detailed View</label>
      <Switch checked={usdsBackingDetailsExpanded} onCheckedChange={setUsdsBackingDetailsExpanded} />
    </div>
  ) : undefined

  const accountRelatedChartsTabs = getAccountRelatedChartsTabs({
    savingsRateChartData,
    savingsToken,
    isChartEnabled: isChartEnabledById[SAVINGS_CHART_IDS.savingsRate],
  })
  const savingsBackedByChartTab = getSavingsBackedByChartTab({
    savingsBackedByChartData,
    underlyingToken,
    controls: {
      usdsBackingDetailsExpanded,
      setUsdsBackingDetailsExpanded,
      detailsSwitch,
    },
    isChartEnabled: isChartEnabledById[SAVINGS_CHART_IDS.savingsBackedBy],
  })
  const liquidityChartTab = getLiquidityChartTab({
    savingsLiquidityChartData,
    underlyingToken,
    immediateLiquidity: savingsLiquidityChartData.immediateLiquidity,
    systemAvailableLiquidity: savingsLiquidityChartData.systemAvailableLiquidity,
    isChartEnabled: isChartEnabledById[SAVINGS_CHART_IDS.liquidity],
  })
  const collateralAtRiskChartTab = getCollateralAtRiskChartTab({
    collateralAtRiskChartData,
    isChartEnabled: isChartEnabledById[SAVINGS_CHART_IDS.collateralAtRisk],
  })
  const tabs = [...accountRelatedChartsTabs]

  if (savingsBackedByChartTab) {
    tabs.push(savingsBackedByChartTab)
  }
  if (liquidityChartTab) {
    tabs.push(liquidityChartTab)
  }
  if (collateralAtRiskChartTab) {
    tabs.push(collateralAtRiskChartTab)
  }

  function handleSelectedTabIdChange(tabId: string): void {
    if (isSavingsChartId(tabId)) {
      onSelectedTabIdChange?.(tabId)
    }
  }

  return tabs.length > 0 ? (
    <ChartTabsPanel tabs={tabs} selectedTabId={selectedTabId} onSelectedTabIdChange={handleSelectedTabIdChange} />
  ) : null
}

interface GetAccountRelatedChartsProps {
  savingsRateChartData: SavingsChartDataById[typeof SAVINGS_CHART_IDS.savingsRate]
  savingsToken: Token
  isChartEnabled: boolean
}

interface BackedByChartControls {
  usdsBackingDetailsExpanded: boolean
  setUsdsBackingDetailsExpanded: (expanded: boolean) => void
  detailsSwitch: ReactNode
}

function getAccountRelatedChartsTabs({
  savingsRateChartData,
  savingsToken,
  isChartEnabled,
}: GetAccountRelatedChartsProps): ChartTab[] {
  const savingsRateChartLabel = savingsToken.symbol === TokenSymbol('stUSDS') ? 'Staked USDS Rate' : 'Savings Rate'

  return isChartEnabled
    ? [
        createChartTab({
          id: SAVINGS_CHART_IDS.savingsRate,
          label: savingsRateChartLabel,
          component: SavingsRateChart,
          isError: savingsRateChartData.queryResult.isError,
          isPending: savingsRateChartData.queryResult.isPending,
          props: {
            data: savingsRateChartData.queryResult.data?.apy ?? [],
            tooltipLabel: savingsRateChartLabel,
            savingsToken,
          },
          timeframeOptions: {
            availableTimeframes: savingsRateChartData.availableTimeframes,
            setSelectedTimeframe: savingsRateChartData.setSelectedTimeframe,
            selectedTimeframe: savingsRateChartData.selectedTimeframe,
          },
        }),
      ]
    : []
}

function getSavingsBackedByChartTab({
  savingsBackedByChartData,
  underlyingToken,
  controls: { usdsBackingDetailsExpanded, setUsdsBackingDetailsExpanded, detailsSwitch },
  isChartEnabled,
}: {
  savingsBackedByChartData: SavingsChartDataById[typeof SAVINGS_CHART_IDS.savingsBackedBy]
  underlyingToken: Token
  controls: BackedByChartControls
  isChartEnabled: boolean
}): ChartTab | undefined {
  if (!isChartEnabled) {
    return undefined
  }

  return createChartTab({
    id: SAVINGS_CHART_IDS.savingsBackedBy,
    label: 'Collateral Composition',
    component: BackedByChart,
    isError: savingsBackedByChartData.queryResult.isError,
    isPending: savingsBackedByChartData.queryResult.isPending,
    headerExtra: detailsSwitch,
    minHeight: 500,
    props: {
      items: savingsBackedByChartData.queryResult.data ?? [],
      underlyingToken,
      groupUsdsDetails: savingsBackedByChartData.groupUsdsDetails,
      detailsExpanded: usdsBackingDetailsExpanded,
      onDetailsExpandedChange: setUsdsBackingDetailsExpanded,
    },
  })
}

function getLiquidityChartTab({
  savingsLiquidityChartData,
  underlyingToken,
  immediateLiquidity,
  systemAvailableLiquidity,
  isChartEnabled,
}: {
  savingsLiquidityChartData: SavingsChartDataById[typeof SAVINGS_CHART_IDS.liquidity]
  underlyingToken: Token
  immediateLiquidity: NormalizedNumber | undefined
  systemAvailableLiquidity: NormalizedNumber | undefined
  isChartEnabled: boolean
}): ChartTab | undefined {
  if (!isChartEnabled) {
    return undefined
  }

  return createChartTab({
    id: SAVINGS_CHART_IDS.liquidity,
    label: 'Liquidity',
    component: LiquidityChart,
    isError: savingsLiquidityChartData.queryResult.isError,
    isPending: savingsLiquidityChartData.queryResult.isPending,
    minHeight: 500,
    props: {
      items: savingsLiquidityChartData.queryResult.data ?? [],
      underlyingToken,
      immediateLiquidity,
      systemAvailableLiquidity,
    },
  })
}

function getCollateralAtRiskChartTab({
  collateralAtRiskChartData,
  isChartEnabled,
}: {
  collateralAtRiskChartData: SavingsChartDataById[typeof SAVINGS_CHART_IDS.collateralAtRisk]
  isChartEnabled: boolean
}): ChartTab | undefined {
  if (!isChartEnabled || !collateralAtRiskChartData) {
    return undefined
  }

  return createChartTab({
    id: SAVINGS_CHART_IDS.collateralAtRisk,
    label: 'Collateral at Risk',
    component: CollateralAtRiskChart,
    isError: collateralAtRiskChartData.isError,
    isPending: collateralAtRiskChartData.isPending,
    props: {
      data: collateralAtRiskChartData.data ?? [],
    },
  })
}
